import {
  ExportReportFormatLabelModifier,
  ReportInvestmentFundsModel,
  ReportInvestmentFundsParamsModel,
  ReportLocalGovernmentFundReportTypeLabelModifier,
  ReportLocalGovernmentFundsParamsModel,
  ReportLocalGovernmentFundsTableAModel,
  ReportLocalGovernmentFundsTableBModel,
  ReportOrganizingTableOrganizationsModel,
  ReportStandardFundReportTypeLabelModifier,
  ReportStandardFundsForPrivateModel,
  ReportStandardFundsForPublicModel,
  ReportStandardFundsParamsModel,
  type reportFileType,
} from '@/features/mold/models';
import { t } from '@/i18n';
import { dateFormat, type jsonType, substrFileName } from '@/utils';
import type { z } from 'zod';
import type {
  rsBatchCsvDownloadTargetType,
  rsBatchPdfDownloadTargetType,
} from '../_models/batch';

export const MAX_FILENAME_LENGTH = 200;

export const getSubstrFileName = (
  fileName: string,
  createdAt: string,
  organizationNames: string[],
  fileType: z.infer<typeof reportFileType>,
) => {
  const organizationsName = organizationNames.join('_');
  const date = dateFormat({ datetime: createdAt, format: 'YYYYMMDD' });
  return substrFileName({
    name: `${fileName}_${date}_${organizationsName}`,
    fileType,
  });
};

export const getExportRsFileName = ({
  jsonData,
  target,
  batchParams,
  fileType,
  dateCreated,
}: {
  jsonData: z.infer<typeof jsonType>;
  target:
    | z.infer<typeof rsBatchPdfDownloadTargetType>
    | z.infer<typeof rsBatchCsvDownloadTargetType>;
  batchParams: z.infer<typeof jsonType>;
  fileType: z.infer<typeof reportFileType>;
  dateCreated: string;
}) => {
  switch (target) {
    case 'rs-project-list': {
      return t('batch.project-list-rs');
    }
    case 'ss-project-list': {
      return t('batch.project-list-ss');
    }
    case 'ks-project-list': {
      return t('batch.project-list-ks');
    }
    case 'ks-investment-list': {
      const { isAllOrganization } =
        ReportInvestmentFundsParamsModel.parse(batchParams);
      const { organizations } = ReportInvestmentFundsModel.parse(jsonData);

      return getSubstrFileName(
        t('report-type-investment-fund'),
        dateCreated,
        isAllOrganization
          ? [t('all-organizations')]
          : organizations.map((v) => v.name),
        fileType,
      );
    }
    case 'ks-standard-list': {
      const { reportType, isAllOrganization } =
        ReportStandardFundsParamsModel.parse(batchParams);
      const { organizations } =
        reportType === 'public'
          ? ReportStandardFundsForPublicModel.parse(jsonData)
          : ReportStandardFundsForPrivateModel.parse(jsonData);
      return getSubstrFileName(
        `${t('report-type-standard-fund')}_${
          ReportStandardFundReportTypeLabelModifier[reportType]
        }`,
        dateCreated,
        isAllOrganization
          ? [t('all-organizations')]
          : organizations.map((v) => v.name),
        fileType,
      );
    }
    case 'ks-local-government-list-A': {
      const { isAllOrganization } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      const { organizations } =
        ReportLocalGovernmentFundsTableAModel.parse(jsonData);

      return getSubstrFileName(
        `${t('report-type-local-government-fund')}_${ReportLocalGovernmentFundReportTypeLabelModifier['table-A']}`,
        dateCreated,
        isAllOrganization
          ? [t('all-organizations')]
          : organizations.map((v) => v.name),
        fileType,
      );
    }
    case 'ks-local-government-list-B': {
      const { isAllOrganization } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      const { organizations } =
        ReportLocalGovernmentFundsTableBModel.parse(jsonData);
      return getSubstrFileName(
        `${t('report-type-local-government-fund')}_${ReportLocalGovernmentFundReportTypeLabelModifier['table-B']}`,
        dateCreated,
        isAllOrganization
          ? [t('all-organizations')]
          : organizations.map((v) => v.name),
        fileType,
      );
    }
    case 'rs-report1':
    case 'rs-report2':
    case 'rs-report3':
    case 'rs-report4':
    case 'rs-report5': {
      const formatLabel = ((): string => {
        switch (target) {
          case 'rs-report1': {
            return 'format1';
          }
          case 'rs-report2': {
            return 'format2';
          }
          case 'rs-report3': {
            return 'format3';
          }
          case 'rs-report4': {
            return 'format4';
          }
          case 'rs-report5': {
            return 'format5';
          }
        }
      })();

      return getSubstrFileName(
        `${t('business-unit-organizational-table')}_${
          ExportReportFormatLabelModifier[formatLabel]
        }`,
        dateCreated,
        ReportOrganizingTableOrganizationsModel.parse(
          jsonData,
        ).organizations.map((v) => v.name),
        fileType,
      );
    }
    case 'payment-group-upload': {
      return t('payment-group-upload-error-file');
    }
    case 'contractor-payment-upload': {
      return t('contractor-payment-upload-error-file');
    }
    case 'organizations-list': {
      return t('organization-list');
    }
    case 'users_download_list': {
      return t('user-list');
    }
    case 'users_upload_list': {
      return t('upload-users-result');
    }
    default: {
      const check: never = target;
      throw new Error(`Unexpected target: ${check}`);
    }
  }
};
